import _differenceWith from 'lodash-es/differenceWith';
import _isEqual from 'lodash-es/isEqual';
import { days, business } from '../../../common/Components/constants';

const getBusinessTypeStringFromEnum = bt => {
  if (bt === 1) return 'Delivery';
  if (bt === 2) return 'Dining';
  return '';
};

export const getBusinessTypesData = optionData => {
  const formattedBusinessTypes = business.map(businessType => ({
    label: businessType?.label,
    value: businessType?.value,
  }));

  const poicySBusinessTypes =
    optionData?.applicable_business_types
      ?.map(businessTypeId => business.find(bt => bt?.value === businessTypeId))
      .filter(Boolean) || [];

  const policyUSelectedBusinessTypes = _differenceWith(
    formattedBusinessTypes,
    poicySBusinessTypes,
    _isEqual,
  );

  return {
    allBusinessTypes: formattedBusinessTypes,
    selectedBusinessTypes: poicySBusinessTypes,
    unSelectedBusinessTypes: policyUSelectedBusinessTypes,
  };
};

export const getDaysData = optionData => {
  const formattedDays = days.map(day => ({
    label: day?.label,
    value: day?.value,
  }));

  const policySelectedDays =
    optionData?.restriction_details?.day_res
      ?.map(dayRes => days.find(day => day?.value === dayRes?.day))
      .filter(Boolean) || formattedDays;

  const policyUSelectedDays = _differenceWith(
    formattedDays,
    policySelectedDays,
    _isEqual,
  );

  return {
    allDays: formattedDays,
    selectedDays: policySelectedDays.sort((a, b) => a?.value - b?.value),
    unSelectedDays: policyUSelectedDays.sort((a, b) => a?.value - b?.value),
  };
};

export const getQuestionLabel = question => {
  let label = question?.title || question?.question_definition || question?.label;
  if (question?.applicable_business_types?.length > 0) {
    label += ` (${question?.applicable_business_types
      .map(bt => getBusinessTypeStringFromEnum(bt))
      .join(', ')})`;
  }
  return label;
};

export const getQuestionsData = (questionsData, optionData, questionStatusEnum) => {
  const formattedQuestions = [];
  questionsData.forEach(question => {
    if (question?.question_status === questionStatusEnum?.ACTIVE)
      formattedQuestions.push({
        label: getQuestionLabel(question),
        value: question?.question_id,
      });
  });

  const policySelectedQuestions =
    optionData?.child_question_details?.map(questionDetail => ({
      label: getQuestionLabel(questionDetail),
      value: questionDetail?.id,
    })) || [];

  const policyUSelectedQuestions = _differenceWith(
    formattedQuestions,
    policySelectedQuestions,
    _isEqual,
  );

  return {
    allQuestions: formattedQuestions,
    selectedQuestions: policySelectedQuestions,
    unSelectedQuestions: policyUSelectedQuestions,
  };
};

export const getLocationsData = locationsData => {
  const formattedLocations = locationsData.map(location => ({
    label: `${location?.address} (${location?.poi_id})`,
    value: location?.poi_id,
  }));
  return { allLocations: formattedLocations };
};

export const getSlotDetails = (dayResArray, allLocations, selectedDays) => {
  const slotsMap = new Map();

  if (dayResArray && dayResArray.length > 0) {
    const getDayObject = dayNumber => days.find(day => day?.value === dayNumber);

    dayResArray.forEach(dayRes => {
      const dayObject = getDayObject(dayRes?.day);
      if (!dayObject) return;

      dayRes?.slot_res?.forEach(slot => {
        const slotKey = `${slot?.type}-${slot?.start_time}-${slot?.end_time}`;

        if (!slotsMap.has(slotKey)) {
          const slotLocations =
            slot?.poi_res?.map(poi => {
              const matchingLocation = allLocations.find(
                loc =>
                  parseInt(loc?.value, 10) === parseInt(poi?.poi_id, 10),
              );
              return (
                matchingLocation || {
                  label: poi?.address || `Location ${poi?.poi_id}`,
                  value: poi?.poi_id,
                  address: poi?.address,
                }
              );
            }) || [];

          const slotUnSelectedLocations = allLocations.filter(
            loc =>
              !slotLocations.some(
                selected =>
                  parseInt(selected?.value, 10) ===
                  parseInt(loc?.value, 10),
              ),
          );

          slotsMap.set(slotKey, {
            id: slotKey,
            startTime: slot?.start_time || '00:00',
            endTime: slot?.end_time || '23:59',
            selectedDays: [dayObject],
            unSelectedDays: [],
            slotBudget: slot?.amount || 0,
            perOrderBudget: slot?.order_res?.per_order_limit_value || 0,
            selectedLocations: slotLocations,
            unSelectedLocations: slotUnSelectedLocations,
            areLocationsPermitted: !slot?.poi_res?.length || slot?.poi_res?.[0]?.apl_rule === 1,
            slotType: slot?.type,
          });
        } else {
          const existingSlot = slotsMap.get(slotKey);
          const dayExists = existingSlot?.selectedDays?.some(
            d => d?.value === dayObject?.value,
          );
          if (!dayExists) existingSlot?.selectedDays?.push(dayObject);
        }
      });
    });

    return Array.from(slotsMap.values()).map(slot => ({
      ...slot,
      selectedDays: slot?.selectedDays?.sort((a, b) => a?.value - b?.value),
      unSelectedDays: selectedDays
        .filter(
          day =>
            !slot?.selectedDays?.some(
              selected => selected?.value === day?.value,
            ),
        )
        .sort((a, b) => a?.value - b?.value),
    }));
  }
  return [];
};

export const validateSlotTimings = slots => {
  if (!slots || slots.length <= 1) return true;

  for (let i = 0; i < slots.length; i += 1) {
    for (let j = i + 1; j < slots.length; j += 1) {
      const slot1 = slots[i];
      const slot2 = slots[j];
      const hasOverlappingDays = slot1?.selectedDays?.some(day1 =>
        slot2?.selectedDays?.some(day2 => day1?.value === day2?.value),
      );

      if (hasOverlappingDays) {
        const start1 = slot1?.startTime || '00:00';
        const end1 = slot1?.endTime || '23:59';
        const start2 = slot2?.startTime || '00:00';
        const end2 = slot2?.endTime || '23:59';

        if (
          (start1 < end2 && end1 > start2) ||
          (start2 < end1 && end2 > start1)
        ) {
          return false;
        }
      }
    }
  }
  return true;
};

export const validateSlotBudgets = slots => {
  if (!slots || slots.length === 0) return { isValidBudget: true };

  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i];
    const slotBudget = parseInt(slot?.slotBudget, 10) || 0;
    if (slotBudget <= 0) {
      return { isValidBudget: false, errorMsg: `Slot budget can't be zero` };
    }
  }
  return { isValidBudget: true };
};

export const validateSlotDaysWithinPolicyDays = (formSelectedDays, slots) => {
  if (!formSelectedDays || formSelectedDays.length === 0) return true;
  if (!slots || slots.length === 0) return true;

  return slots.every(slot => {
    if (!slot?.selectedDays || slot?.selectedDays?.length === 0) return true;
    return slot?.selectedDays?.every(slotDay =>
      formSelectedDays.some(formDay => formDay?.value === slotDay?.value),
    );
  });
};

export const validatePerOrderBudgets = slots => {
  if (!slots || slots.length === 0) return true;
  return slots.every(slot => {
    const slotBudget = parseInt(slot?.slotBudget, 10) || 0;
    const perOrderBudget = parseInt(slot?.perOrderBudget, 10) || 0;
    if (perOrderBudget > 0 && slotBudget > 0) return perOrderBudget <= slotBudget;
    return true;
  });
};

export const validateDaysHaveSlots = (formSelectedDays, slots) => {
  if (!formSelectedDays || formSelectedDays.length === 0) return true;
  if (!slots || slots.length === 0) return false;

  return formSelectedDays.every(formDay =>
    slots.some(
      slot =>
        slot?.selectedDays &&
        slot?.selectedDays?.some(slotDay => slotDay?.value === formDay?.value),
    ),
  );
};

export const getUnixTimeForToday = timeStr => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr?.split(':')?.map(Number) || [0, 0];
  const now = new Date();
  const date = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0,
  );
  return Math.floor(date.getTime() / 1000);
};

export const validateSlotsProgramStartTime = (slots, programStartTime) => {
  if (!slots || slots?.length === 0 || !programStartTime) return true;
  const programStartUnix = getUnixTimeForToday(programStartTime);
  const programEndUnix = programStartUnix + 24 * 60 * 60 - 1;

  for (let i = 0; i < slots?.length; i += 1) {
    const slot = slots?.[i];
    const slotStartUnix = getUnixTimeForToday(slot?.startTime || '00:00');
    let slotEndUnix = getUnixTimeForToday(slot?.endTime || '23:59');
    if (slotEndUnix < slotStartUnix) slotEndUnix += 24 * 60 * 60;
    const contained =
      slotStartUnix >= programStartUnix && slotEndUnix <= programEndUnix;
    if (!contained) return false;
  }
  return true;
};

export const validateSlotData = (formData, programStartTime) => {
  if (formData?.isAdvancedRulesEnabled && formData?.slots?.length > 0) {
    if (!validateSlotTimings(formData?.slots)) {
      return { isValid: false, errorMsg: "Two different slot timings can't overlap" };
    }
    if (programStartTime) {
      if (!validateSlotsProgramStartTime(formData?.slots, programStartTime)) {
        return {
          isValid: false,
          errorMsg: 'Slot level time range should be within the plan time range',
        };
      }
    }
    if (!validateDaysHaveSlots(formData?.selectedDays, formData?.slots)) {
      return {
        isValid: false,
        errorMsg: 'Days set at policy level must be included in at least one slot rule',
      };
    }
    if (!validateSlotDaysWithinPolicyDays(formData?.selectedDays, formData?.slots)) {
      return { isValid: false, errorMsg: 'Slot days must fall within the policy days' };
    }
    const { isValidBudget, errorMsg } = validateSlotBudgets(formData?.slots);
    if (!isValidBudget) return { isValid: false, errorMsg };
    if (!validatePerOrderBudgets(formData?.slots)) {
      return {
        isValid: false,
        errorMsg: 'Per order budget can not be greater than slot budget',
      };
    }
  }
  return { isValid: true };
};

export const createNewSlot = (formData, allLocations) => ({
  id: `new-slot-${Date.now()}`,
  startTime: '00:00',
  endTime: '23:59',
  selectedDays: [...formData?.selectedDays],
  unSelectedDays: [],
  slotBudget: 0,
  perOrderBudget: 0,
  selectedLocations: [],
  unSelectedLocations: [...allLocations],
  areLocationsPermitted: true,
});

export const updatePolicyUnSelectedDays = updateFormData => newDays => {
  updateFormData({ unSelectedDays: newDays });
};

export const updateSelectedBusinessTypes = updateFormData => newBusinessTypes => {
  updateFormData({ selectedBusinessTypes: newBusinessTypes, showError: false });
};

export const updateUnSelectedBusinessTypes = updateFormData => newBusinessTypes => {
  updateFormData({ unSelectedBusinessTypes: newBusinessTypes, showError: false });
};

export const updatePolicySelectedDays = updateFormData => newDays => {
  updateFormData({ selectedDays: newDays, showError: false });
};

export const updateSelectedQuestion = updateFormData => newQuestion => {
  updateFormData({ selectedQuestions: newQuestion, showError: false });
};

export const updateUnSelectedQuestion = updateFormData => newQuestion => {
  updateFormData({ unSelectedQuestions: newQuestion });
};

export const updateSlotStartTime =
  (updateFormData, formData) => slotIndex => newStartTime => {
    const updatedSlots = [...formData?.slots];
    if (updatedSlots.length <= slotIndex) return;
    updatedSlots[slotIndex].startTime = newStartTime || '00:00';
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotEndTime =
  (updateFormData, formData) => slotIndex => newEndTime => {
    const updatedSlots = [...formData?.slots];
    if (updatedSlots.length <= slotIndex) return;
    updatedSlots[slotIndex].endTime = newEndTime || '23:59';
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotSelectedDays =
  (updateFormData, formData) => (slotIndex, newDays) => {
    const updatedSlots = [...formData?.slots];
    const availableDays = newDays.filter(day =>
      formData?.selectedDays?.some(formDay => formDay?.value === day?.value),
    );
    updatedSlots[slotIndex].selectedDays = availableDays;
    updatedSlots[slotIndex].unSelectedDays = formData?.selectedDays?.filter(
      formDay =>
        !availableDays.some(slotDay => slotDay?.value === formDay?.value),
    );
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotUnSelectedDays =
  (updateFormData, formData) => (slotIndex, newUnSelectedDays) => {
    const updatedSlots = [...formData?.slots];
    updatedSlots[slotIndex].unSelectedDays = newUnSelectedDays;
    updateFormData({ slots: updatedSlots });
  };

export const updateSlotBudget =
  (updateFormData, formData) => slotIndex => newBudget => {
    const updatedSlots = [...formData?.slots];
    if (updatedSlots.length <= slotIndex) return;
    updateFormData({ showError: false });
    const val = parseInt(newBudget, Number);
    if (val >= 0) updatedSlots[slotIndex].slotBudget = val;
    else if (!Number.isInteger(val)) updatedSlots[slotIndex].slotBudget = '';
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotPerOrderBudget =
  (updateFormData, formData) => slotIndex => newPerOrderBudget => {
    const updatedSlots = [...formData?.slots];
    if (updatedSlots.length <= slotIndex) return;
    updateFormData({ showError: false });
    const val = parseInt(newPerOrderBudget, Number);
    if (val >= 0) updatedSlots[slotIndex].perOrderBudget = val;
    else if (!Number.isInteger(val)) updatedSlots[slotIndex].perOrderBudget = '';
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotSelectedLocations =
  (updateFormData, formData, allLocations) => (slotIndex, newLocations) => {
    const updatedSlots = [...formData?.slots];
    updatedSlots[slotIndex].selectedLocations = newLocations;
    updatedSlots[slotIndex].unSelectedLocations = allLocations.filter(
      location =>
        !newLocations.some(
          selected => selected?.value === location?.value,
        ),
    );
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const updateSlotUnSelectedLocations =
  (updateFormData, formData) => (slotIndex, newUnSelectedLocations) => {
    const updatedSlots = [...formData?.slots];
    updatedSlots[slotIndex].unSelectedLocations = newUnSelectedLocations;
    if (updatedSlots[slotIndex]?.selectedLocations?.length === 0) {
      updatedSlots[slotIndex].areLocationsPermitted = true;
    }
    updateFormData({ slots: updatedSlots });
  };

export const updateSlotLocationPermission =
  (updateFormData, formData) => slotIndex => {
    const updatedSlots = [...formData?.slots];
    updatedSlots[slotIndex].areLocationsPermitted =
      !updatedSlots[slotIndex]?.areLocationsPermitted;
    updateFormData({ slots: updatedSlots, showError: false });
  };

export const handleDeleteSlots =
  (updateFormData, formData) => index => () => {
    const updatedSlots = [...formData?.slots];
    if (updatedSlots.length <= index) return;
    updatedSlots.splice(index, 1);
    updateFormData({
      slots: updatedSlots,
      isAdvancedRulesEnabled: updatedSlots.length > 0,
    });
  };

export const handleCycleTypeSelect = updateFormData => val => {
  updateFormData({ cycleType: val, amount: '' });
};

export const addNewSlot = (updateFormData, formData, allLocations) => () => {
  const newSlot = createNewSlot(formData, allLocations);
  updateFormData({
    slots: [...formData.slots, newSlot],
    showError: false,
  });
};
